import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { useCallerInfo } from '../../hooks/use-caller-info';
import { useSoftphoneCallState } from '../../providers/softphone-call-state-provider';
import { useSoftphoneDirectory } from '../../providers/softphone-directory-provider';
import { useSoftphoneWidgetControl } from '../../providers/widget-control-provider';
import { IncomingCallBarContent } from './incoming-cb-content';
import { IncomingCallBarHead } from './incoming-cb-head';
import { IncomingCallBarTrail } from './incoming-cb-trail';

export const IncomingCallBar = ({
  setExpandedIncomingCallBar,
  canCollapseCallBar,
}: {
  setExpandedIncomingCallBar: (val: boolean) => void;
  canCollapseCallBar: boolean;
}) => {
  const incomingCalls = useSoftphoneCallState((ctx) => ctx.incomingCalls);
  const useGetPersonImageQuery = useSoftphoneDirectory((ctx) => ctx.queries.useGetPersonImageQuery);
  const mergedCallGroup = useSoftphoneCallState((ctx) => ctx.mergedCallGroup);
  const [position, setPosition] = useState(1);
  const isMobile = useSoftphoneWidgetControl((ctx) => ctx.isMobile);

  const latestIncomingCall = useMemo(() => {
    if (incomingCalls.length > 1) {
      return incomingCalls[position - 1];
    } else if (incomingCalls.length === 1) {
      return incomingCalls[0];
    }

    return undefined;
  }, [incomingCalls, position]);

  const { contact, title } = useCallerInfo({
    uri: latestIncomingCall?.remoteParty.uri ?? '',
    displayName: latestIncomingCall?.remoteParty.displayName,
    enabled: !mergedCallGroup && !!latestIncomingCall,
  });

  const { data: image } = useGetPersonImageQuery(contact?.PersonID ?? '', {
    enabled: !!latestIncomingCall && !!contact && !mergedCallGroup,
  });

  if (!latestIncomingCall) {
    return null;
  }

  return (
    <section id='softphone-incoming-call-bar-widget' css={callBarContainer(isMobile)}>
      <div css={{ gridArea: 'lead' }} className='lead'>
        <IncomingCallBarHead image={image} title={title} contact={contact} incomingCall={latestIncomingCall} />
      </div>
      <div css={{ gridArea: 'content' }} className='content'>
        <IncomingCallBarContent incomingCall={latestIncomingCall} />
      </div>
      <div css={{ gridArea: 'trail' }} className='trail'>
        <IncomingCallBarTrail
          canCollapseCallBar={canCollapseCallBar}
          incomingCalls={incomingCalls}
          setExpandedIncomingCallBar={setExpandedIncomingCallBar}
          position={position}
          setPosition={setPosition}
        />
      </div>
    </section>
  );
};

export const callBarContainer = (isMobile: boolean) => css`
  min-width: 344px;
  max-width: 344px;
  background-color: ${theme.colors.neutral90};
  border-radius: ${theme.borderRadius.medium};
  width: 100%;
  display: grid;
  grid-template-areas: 'lead content trail';
  grid-template-columns: auto 1fr auto;
  align-items: center;
  cursor: pointer;
  padding: ${theme.spacing(1)};
  z-index: ${theme.zIndex.modal};
  -webkit-tap-highlight-color: transparent;
  -moz-tap-highlight-color: transparent;
  -o-tap-highlight-color: transparent;
  tap-highlight-color: transparent;
  box-shadow: 0 0 8px 4px rgba(8, 135, 63, 0.5);

  .lead,
  .trail {
    display: flex;
    align-items: center;
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: ${theme.spacing(0.75)};
    min-width: 0;
  }

  .content {
    display: flex;
    justify-content: end;
    align-items: center;
    padding: ${theme.spacing(0, 1)};
    flex: 1;
    gap: ${theme.spacing(1)};
  }

  ${isMobile &&
  `
    max-width: 100%;
    border-radius: 0;
    `}
`;
